/* @flow */

import React, { useCallback } from 'react';

import type { Profile } from '../../../helpers/profiles/typesAndConstants';

import { logError } from '../../../helpers/debug/debug';
import Localizer from '../../../helpers/localization';
import { isProfilePrivate } from '../../../helpers/profiles/helpers';
import Avatar from '../../profiles/Avatar';
import { AvatarSize, DisplayNamePlacement, EditMode } from '../../profiles/types';

type ProfileViewPropType = {
  +isEditMode?: boolean,
  +isSelected?: boolean,
  +onClick?: (id: string) => Promise<void> | void,
  +profile: Profile,
};

const ProfileView = ({ isEditMode = false, isSelected = false, onClick, profile }: ProfileViewPropType): React.Node => {
  const handleOnClick = useCallback(() => {
    if (!onClick) {
      return;
    }

    const result = onClick(profile.id);

    if (result instanceof Promise) {
      result.catch((error) => {
        logError(`Error in ProfileView handleOnClick: ${error.message}`);
      });
    }
  }, [profile]);

  const getAriaLabelKey = useCallback(() => {
    const { isKid } = profile;
    const isPrivate = isProfilePrivate(profile);

    if (isEditMode) {
      if (isPrivate) {
        return 'accessibility.profiles.edit_profile_private';
      }
      if (isKid) {
        return 'accessibility.profiles.edit_profile_kid';
      }
      return 'accessibility.profiles.edit_profile';
    } else {
      if (isPrivate) {
        return 'accessibility.profiles.select_profile_private';
      }
      if (isKid) {
        return 'accessibility.profiles.select_profile_kid';
      }
      return 'accessibility.profiles.select_profile';
    }
  }, [isEditMode, profile]);

  const { avatarId, id, isKid, name } = profile;

  const ariaLabel = typeof onClick === 'function' ? Localizer.localize(getAriaLabelKey(), { name }) : '';

  return (
    <Avatar
      aria-label={ariaLabel}
      avatarId={avatarId}
      cutIfTooLong
      displayName={DisplayNamePlacement.Bottom}
      editMode={isEditMode ? EditMode.ShowAlways : EditMode.Hidden}
      isDisabled={typeof onClick !== 'function'}
      isKid={isKid}
      isPrivate={isProfilePrivate(profile)}
      isSelected={isSelected}
      name={name}
      onClick={handleOnClick}
      profileId={id}
      size={AvatarSize.Big}
    />
  );
};

export default ProfileView;
